import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Star, Sparkles, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import VibrationalFrequencyInsights from './VibrationalFrequencyInsights';

const frequencyActivities = [
  { id: 'meditation', label: 'Meditation' },
  { id: 'sitting_in_power', label: 'Sitting in the Power' },
  { id: 'nature', label: 'Time in Nature' },
  { id: 'music', label: 'High Vibe Music' },
  { id: 'gratitude', label: 'Gratitude Practice' },
  { id: 'movement', label: 'Movement/Dance' },
  { id: 'laughter', label: 'Joy/Laughter' },
  { id: 'service', label: 'Acts of Service' },
  { id: 'creativity', label: 'Creative Expression' },
  { id: 'other', label: 'Other Practice' }
];

export default function VibrationalFrequency() {
  const [frequency, setFrequency] = useState(5);
  const [mood, setMood] = useState('');
  const [readingQuality, setReadingQuality] = useState(5);
  const [activities, setActivities] = useState<string[]>([]);
  const [observations, setObservations] = useState('');
  const [visualizationNotes, setVisualizationNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const getVibeRange = (value: number) => {
    if (value <= 3) return 'low';
    if (value <= 7) return 'mid';
    return 'high';
  };

  const vibeRange = getVibeRange(frequency);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to save your data.",
          variant: "destructive"
        });
        return;
      }

      const today = new Date().toISOString().split('T')[0];
      
      const { error } = await supabase
        .from('vibrational_frequency')
        .upsert({
          user_id: user.id,
          date: today,
          frequency,
          mood,
          reading_quality: readingQuality,
          activities,
          observations,
          visualization_notes: visualizationNotes,
          vibe_range: vibeRange,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,date'
        });

      if (error) throw error;

      toast({
        title: "Saved!",
        description: "Your frequency data has been logged successfully."
      });

      // Reset form
      setFrequency(5);
      setMood('');
      setReadingQuality(5);
      setActivities([]);
      setObservations('');
      setVisualizationNotes('');
    } catch (error: any) {
      console.error('Error saving frequency data:', error);
      toast({
        title: "Could not save",
        description: error.message || "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <VibrationalFrequencyInsights />
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-primary animate-pulse" />
            Vibrational Frequency Tracker
          </CardTitle>
          <CardDescription>
            Track your energetic state and discover how your vibration affects psychic receptivity and reading quality. Higher frequencies enhance clarity and connection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
            <div className={`text-center p-3 rounded-lg transition-all ${vibeRange === 'low' ? 'bg-destructive/20 border border-destructive/40' : ''}`}>
              <p className="text-xs font-medium text-muted-foreground mb-1">LOW VIBE (1-3)</p>
              <p className="text-xs">😔 Fear, Shame, Guilt</p>
              <p className="text-xs text-muted-foreground mt-1">Dense, contracted</p>
            </div>
            <div className={`text-center p-3 rounded-lg transition-all ${vibeRange === 'mid' ? 'bg-accent/20 border border-accent/40' : ''}`}>
              <p className="text-xs font-medium text-muted-foreground mb-1">MID VIBE (4-7)</p>
              <p className="text-xs">😐 Neutral, Courage</p>
              <p className="text-xs text-muted-foreground mt-1">Opening, willing</p>
            </div>
            <div className={`text-center p-3 rounded-lg transition-all ${vibeRange === 'high' ? 'bg-primary/20 border border-primary/40' : ''}`}>
              <p className="text-xs font-medium text-muted-foreground mb-1">HIGH VIBE (8-10)</p>
              <p className="text-xs">✨ Joy, Love, Peace</p>
              <p className="text-xs text-muted-foreground mt-1">Expansive, receptive</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Current Vibrational Frequency: {frequency}/10</Label>
            <Slider
              value={[frequency]}
              onValueChange={([value]) => setFrequency(value)}
              min={1}
              max={10}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground font-light px-1">
              <span>Contracted</span>
              <span>Opening</span>
              <span>Expansive</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Emotional State</Label>
            <Select value={mood} onValueChange={setMood}>
              <SelectTrigger>
                <SelectValue placeholder="How are you feeling?" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="joyful">Joyful & Elevated</SelectItem>
                <SelectItem value="grateful">Grateful & Appreciative</SelectItem>
                <SelectItem value="peaceful">Peaceful & Calm</SelectItem>
                <SelectItem value="neutral">Neutral</SelectItem>
                <SelectItem value="anxious">Anxious or Worried</SelectItem>
                <SelectItem value="frustrated">Frustrated or Angry</SelectItem>
                <SelectItem value="sad">Sad or Low Energy</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Psychic Reading Quality Today: {readingQuality}/10</Label>
            <Slider
              value={[readingQuality]}
              onValueChange={([value]) => setReadingQuality(value)}
              min={1}
              max={10}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Rate the clarity and accuracy of your psychic impressions today
            </p>
          </div>

          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Frequency-Raising Activities Done Today
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {frequencyActivities.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={activity.id}
                    checked={activities.includes(activity.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setActivities([...activities, activity.id]);
                      } else {
                        setActivities(activities.filter(id => id !== activity.id));
                      }
                    }}
                  />
                  <label htmlFor={activity.id} className="text-sm cursor-pointer">
                    {activity.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Observations & Patterns</Label>
            <Textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Notice any correlation between your vibe and psychic clarity? What raises or lowers your frequency?"
              rows={3}
            />
          </div>

          <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
            <h4 className="font-light text-sm mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-accent" />
              Frequency Visualisation Practice
            </h4>
            <p className="text-xs text-muted-foreground font-light mb-3">
              Connect with the version of you that already embodies the frequency you desire. Visualisation has been scientifically shown to activate the same neural pathways as actual experience, influencing neurology, physiology, and biochemistry.
            </p>
            <Textarea
              value={visualizationNotes}
              onChange={(e) => setVisualizationNotes(e.target.value)}
              placeholder="Describe the version of you at your desired frequency... What do they feel? How do they move? What energy do they radiate?"
              rows={3}
            />
          </div>

          <Button onClick={handleSave} disabled={isLoading} className="w-full">
            <TrendingUp className="h-4 w-4 mr-2" />
            Log Frequency
          </Button>

          <div className="space-y-4">
            <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
              <h4 className="font-light text-primary mb-2">🌟 Understanding Vibrational Frequency</h4>
              <p className="text-sm text-muted-foreground font-light mb-2">
                Your vibrational frequency determines what you can perceive, attract, and receive. Higher frequencies create clearer psychic reception and stronger spiritual connection.
              </p>
              <ul className="text-sm space-y-1 text-muted-foreground font-light">
                <li>• <strong>Low vibe (1-3):</strong> Fear, shame, guilt, apathy - dense, contracted energy</li>
                <li>• <strong>Mid vibe (4-7):</strong> Courage, willingness, acceptance - opening, transitional</li>
                <li>• <strong>High vibe (8-10):</strong> Love, joy, peace, enlightenment - expansive, receptive</li>
              </ul>
            </div>

            <div className="p-4 bg-secondary/10 rounded-lg border border-secondary/20">
              <h4 className="font-light text-foreground mb-2">⚡ Sitting in the Power</h4>
              <p className="text-sm text-muted-foreground font-light mb-2">
                This foundational practice prepares your energy field to shift into the receptive frequency needed for psychic work. Regular practice strengthens your ability to consciously raise and stabilise your vibration.
              </p>
              <p className="text-xs text-muted-foreground font-light italic mb-3">
                The more you practice, the easier it becomes to access higher frequencies at will.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const element = document.getElementById('third-eye-development');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="w-full"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Practice Sitting in the Power Now
              </Button>
            </div>

            <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
              <h4 className="font-light text-accent mb-2">🧠 The Science of Visualisation</h4>
              <p className="text-sm text-muted-foreground font-light mb-2">
                Research shows that visualisation activates the same neural pathways as lived experience, creating measurable changes in:
              </p>
              <ul className="text-sm space-y-1 text-muted-foreground font-light">
                <li>• <strong>Neurology:</strong> New neural connections form as you repeatedly visualise desired states</li>
                <li>• <strong>Physiology:</strong> Heart rate, breathing, and muscle tension respond to imagined scenarios</li>
                <li>• <strong>Biochemistry:</strong> Hormones and neurotransmitters shift based on visualised emotions</li>
              </ul>
              <p className="text-xs text-muted-foreground font-light italic mt-2">
                Your nervous system doesn't distinguish between vividly imagined and actual experience - use this to your advantage.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
